import documentIcon from '@/assets/document_icon.png';
import { ParcelDetails } from "@/types";
import { Box, Grid, Image, ScrollArea, Stack, Text } from "@mantine/core";

interface ParcelDetailProps {
    parcelDetails: ParcelDetails;
}

const ParcelDetail: React.FC<ParcelDetailProps> = ({ parcelDetails }) => {
    const formatDate = (date: number | undefined) => {
        const dateString = date?.toString() ?? '';
        if (dateString.length === 8) {
            return `${dateString.substring(4, 6)}-${dateString.substring(6, 8)}-${dateString.substring(0, 4)}`
        }
        return '';
    }
    const formatCurrency = (amount: number | undefined) => {
        return amount ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(amount) : '';
    }
    const formatNumber = (number: number | undefined, significantDigits: number = 2) => {
        return number ? new Intl.NumberFormat("en-US", { maximumFractionDigits: significantDigits, minimumFractionDigits: 0 }).format(number) : '';
    }

    const formatAcres = (acres: number | undefined) => {
        return acres ? formatNumber(acres / 1000, 3) : '';
    }
    return (
        <ScrollArea h='100%' pt={5} type='never' styles={{viewport: {overflowX: 'hidden'}}}>
            <Grid align='center'>
                <Grid.Col span={2}>
                    <Box>
                        <Image fit='contain' src={documentIcon} alt='Document Icon' h={50} />
                    </Box>
                </Grid.Col>
                <Grid.Col span={10}>
                    <Stack gap={0}>
                        <Text fw={700} size="md">{parcelDetails.names}</Text>
                        <Text size="sm">{parcelDetails.s_addr_d}, {parcelDetails.s_city} {parcelDetails.s_st} {parcelDetails.s_zip}</Text>
                        <Text size="sm">{parcelDetails.county_name}</Text>
                    </Stack>
                </Grid.Col>
            </Grid>
            <Box pb='calc(env(safe-area-inset-bottom, 0px) + 60px)'>
                <p>
                    Parcel # (APN): <strong>{parcelDetails.apn_d}</strong> <br />
                    Mailing Address: <strong>{parcelDetails.m_addr_d}</strong> <br />
                    Mailing City St Zip: <strong>{parcelDetails.m_city} {parcelDetails.m_st} {parcelDetails.m_zip}</strong> <br />
                    Legal Desc: <strong>{parcelDetails.legal}</strong>
                </p>
                <p>
                    <span color="#b31e3b;"><strong>ASSESSMENT INFO:</strong></span><br />
                    Year Assessed: <strong>{parcelDetails.yr_assd?.toString()}</strong><br />
                    Total Value: <strong>{formatCurrency(parcelDetails.gross_val)}</strong><br />
                    Land Value: <strong>{formatCurrency(parcelDetails.land_val)}</strong><br />
                    Improvement Value: <strong>{formatCurrency(parcelDetails.impr_val)}</strong> <br />
                    Other Value: <strong>{formatCurrency(parcelDetails.other_val)}</strong> <br />
                    % Improved: <strong>{parcelDetails.ratio?.toString()}%</strong><br />
                    Exempt Amount: <strong>{formatCurrency(parcelDetails.exe_total)}   </strong><br />
                    {/*Owner Occupied: <strong>{parcelDetails.ho_fl}</strong> <br />*/}
                    Use Type: <strong>{parcelDetails.use_std}</strong> <br />
                    Use Code: <strong>{parcelDetails.use_co}</strong> <br />
                    Tax Rate Area: <strong>{parcelDetails.tra?.substring(0,3)}-{parcelDetails.tra?.substring(3)}</strong><br />
                    Property Tax: <strong>{formatCurrency(parcelDetails.tax_amt)}</strong> <br />
                    Delinquent Year: <strong>{parcelDetails.del_yr?.toString()}</strong> <br />
                    HO Exempt: <strong>{parcelDetails.ho_fl}</strong><br />
                    PQ Zoning: <strong>{parcelDetails.zoning}</strong> <br />
                    Census Tract: <strong>{parcelDetails.n_tract} </strong><br />
                    Price/Sq Ft:
                </p>
                <p>
                    <span color="#b31e3b;"><strong>SALE HISTORY:</strong></span><br />
                    <strong><u>Sale 1</u></strong><br />
                    Document Date: <strong>{formatDate(parcelDetails.sale1_date)}</strong><br />
                    Document Number: <strong>{parcelDetails.sale1_doc}</strong><br />
                    Document Type: <strong>{parcelDetails.sale1_doctype}</strong><br />
                    Transfer Amount: <strong>{formatCurrency(parcelDetails.sale1_amt)}</strong><br />
                    Seller (Grantor): <strong>{parcelDetails.seller}</strong><br />
                    <br />
                    <strong><u>Sale 2</u></strong><br />
                    Document Date: <strong>{formatDate(parcelDetails.sale2_date)}</strong> <br />
                    Document Number: <strong>{parcelDetails.sale2_doc}</strong> <br />
                    Document Type: <strong>{parcelDetails.sale2_doctype}</strong><br />
                    Transfer Amount: <strong>{formatCurrency(parcelDetails.sale2_amt)}</strong> <br />
                    <br />
                    <strong><u>Sale 3</u></strong><br />
                    Document Date: <strong>{formatDate(parcelDetails.sale3_date)}</strong> <br />
                    Document Number: <strong>{parcelDetails.sale3_doc}</strong> <br />
                    Document Type: <strong>{parcelDetails.sale3_doctype}</strong><br />
                    Transfer Amount: <strong>{formatCurrency(parcelDetails.sale3_amt)}</strong> <br />
                    <br />
                    <strong><u>Transfer</u></strong><u></u><br />
                    Transfer Date: <strong>{formatDate(parcelDetails.xfer_date)}</strong> <br />
                    Document Number: <strong>{parcelDetails.xfer_doc}</strong> <br />
                </p>
                <p>
                    <span color="#b31e3b;"><strong>PROPERTY CHARACTERISTICS:</strong></span><br />
                    Beds: <strong>{formatNumber(parcelDetails.beds)}</strong> <br />
                    Baths (Full): <strong>{formatNumber(parcelDetails.bath_f)}</strong> <br />
                    Baths (Half): <strong>{formatNumber(parcelDetails.bath_h)}</strong> <br />
                    Total Rooms: <strong>{formatNumber(parcelDetails.rooms)}</strong> <br />
                    Building/Living Area: <strong>{formatNumber(parcelDetails.bldg_sqft)}</strong> <br />
                    Lot Acres: <strong>{formatAcres(parcelDetails.lot_acres)}</strong> <br />
                    Lot SqFt: <strong>{formatNumber(parcelDetails.lot_sqft)}</strong> <br />
                    Year Built: <strong>{parcelDetails.yr_blt?.toString()}</strong> <br />
                    Effective Year: <strong>{parcelDetails.yr_eff?.toString()}</strong> <br />
                    Fireplace: <strong>{parcelDetails.fire_pl}</strong> <br />
                    A/C: <strong>{parcelDetails.air_cond}</strong><br />
                    Heating: <strong>{parcelDetails.heat}</strong><br />
                    Pool: <strong>{parcelDetails.pool}</strong><br />
                    Park Type: <strong>{parcelDetails.park_type}</strong><br />
                    Spaces: <strong>{formatNumber(parcelDetails.park_space)}</strong><br />
                    Garage SqFt: <strong>{formatNumber(parcelDetails.gar_sqft)}</strong><br />
                    Units: <strong>{formatNumber(parcelDetails.units)}</strong><br />
                    Story: <strong>{parcelDetails.stories}</strong><br />
                    Quality: <strong>{parcelDetails.bldg_quality}</strong><br />
                    Building Class: <strong>{parcelDetails.bldg_cls}</strong><br />
                    Condition: <strong>{parcelDetails.impr_cond}</strong><br />
                    Site Influence: <strong>{formatNumber(parcelDetails.homesite)}</strong><br />
                    Timber Preserve: <strong>{parcelDetails.timber}</strong><br />
                    Ag Preserve: <strong>{parcelDetails.ag_pre}</strong><br />
                </p>
            </Box>
        </ScrollArea>
    )
}

export default ParcelDetail;